import { ContentAction, ContentLocalizationStatus, Permission, type Role } from '@today/types'

/** Which content-admin controls the viewer may see (TO-420 content-admin-ui REQ-001). */
export interface ContentCapabilities {
	read: boolean
	write: boolean
	approve: boolean
	publish: boolean
	assign: boolean
	manageTypes: boolean
}

/** The viewer as the review rules see it: `/auth/me` id, role and permission atoms. */
export interface ReviewViewer {
	userId: string
	role: string
	permissions: readonly string[]
}

export interface ReviewType {
	approverRoles: readonly Role[]
	forbidSelfApproval: boolean
}

export interface ReviewLocalization {
	authoredBy: string
	status: ContentLocalizationStatus
}

/** The pure permission map: a control the viewer lacks is not rendered. */
export const contentCapabilities = (permissions: readonly string[]): ContentCapabilities => {
	const has = (permission: Permission) => permissions.includes(permission)
	return {
		read: has(Permission.CONTENT_READ),
		write: has(Permission.CONTENT_WRITE),
		approve: has(Permission.CONTENT_APPROVE),
		publish: has(Permission.CONTENT_PUBLISH),
		assign: has(Permission.CONTENT_ASSIGN),
		manageTypes: has(Permission.CONTENT_TYPES_MANAGE),
	}
}

/** The user who submitted the localization for review: the newest `submit` row of its track. */
export const submitterOf = (track: readonly { action: string; userId: string | null }[]): string | null =>
	[...track].reverse().find((row) => row.action === ContentAction.SUBMIT)?.userId ?? null

/**
 * An eligible approver: holds `content.approve`, has one of the type's approver
 * roles and, under four-eyes, is neither the author nor the submitter.
 */
export const canApprove = (
	viewer: ReviewViewer,
	type: ReviewType,
	localization: ReviewLocalization,
	submitter: string | null,
): boolean => {
	if (localization.status !== ContentLocalizationStatus.IN_REVIEW) return false
	if (!viewer.permissions.includes(Permission.CONTENT_APPROVE)) return false
	if (!type.approverRoles.some((role) => role === viewer.role)) return false
	if (!type.forbidSelfApproval) return true
	return viewer.userId !== localization.authoredBy && viewer.userId !== submitter
}

/** "Pull back" (a reject with a reason) is the author's action when Reject is not already offered. */
export const canPullBack = (viewer: ReviewViewer, localization: ReviewLocalization, approver: boolean): boolean =>
	localization.status === ContentLocalizationStatus.IN_REVIEW &&
	!approver &&
	viewer.userId === localization.authoredBy &&
	viewer.permissions.includes(Permission.CONTENT_WRITE)
